import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
} from '@mui/material';
import { put } from 'src/services';
import { authenticationService } from 'src/services/authentication.service';

interface EditTaxProps {
  taxData: any;
  handleClose: () => void;
  handleRefresh: () => void;
  showSnackbar: (message: string, severity: 'success' | 'error') => void;
}

const EditTax: React.FC<EditTaxProps> = ({ taxData, handleClose, handleRefresh, showSnackbar }) => {
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  const [loading, setLoading] = useState(false);
  
  const taxCategories = [
    { id: 1, name: 'CGST' },
    { id: 2, name: 'IGST'},
    { id: 3, name: 'SGST'},
    { id: 4, name: 'Other Expense' },
    { id: 5, name: 'Other Income' },
  ];

  // Static data for Account Names (will be replaced with API call later)
  const accountNames = [
    { id: 1, name: 'CGST 9%' },
    { id: 2, name: 'ENTERTAMENT TAX' },
    { id: 3, name: 'General Tax a/c' },
    { id: 4, name: 'GST' },
    { id: 5, name: 'GST NILL' },
    { id: 6, name: 'IGST 9%' },
    { id: 7, name: 'OneTime Charger2' },
    { id: 8, name: 'S SESS' },
    { id: 9, name: 'SCERVICES TAX' },
    { id: 10, name: 'SGST 9%' },
  ];
  
  const [formData, setFormData] = useState({
    id: taxData.id,
    taxType: taxData.taxType || '',
    value: taxData.value || '',
    calculationOn: taxData.calculationOn || '',
    calculationOrder: taxData.calculationOrder || 0,
    taxCategory: taxData.taxCategory || '',
    accountName: taxData.accountName || '',
    percentage: taxData.percentage === 'Yes' || taxData.percentage === true,
    addition: taxData.addition === 'Yes' || taxData.addition === true,
    status: taxData.status || 'Active',
    vendorId: vendorId  // Make sure vendorId is included
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [name]: event.target.checked }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.taxType) {
      showSnackbar('Please select Tax Type', 'error');
      return;
    }
    if (!formData.value) {
      showSnackbar('Please enter Value', 'error');
      return;
    }
    if (!formData.calculationOn) {
      showSnackbar('Please select Calculation On', 'error');
      return;
    }
    if (!formData.taxCategory) {
      showSnackbar('Please select Tax Category', 'error');
      return;
    }
    if (!formData.accountName) {
      showSnackbar('Please select Account Name', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: formData.id,
        taxType: formData.taxType,
        value: parseFloat(formData.value.toString()),
        calculationOn: formData.calculationOn,
        calculationOrder: parseInt(formData.calculationOrder.toString()),
        taxCategory: formData.taxCategory,
        accountName: formData.accountName,
        percentage: formData.percentage,
        addition: formData.addition,
        status: formData.status,
        vendorId: formData.vendorId  // Include vendorId in payload
      };

      console.log('Update payload:', payload); // Debug log

      const response = await put(`${process.env.REACT_APP_BASE_API_URL}/api/tax/update`, payload);
      
      console.log('Update response:', response); // Debug log
      
      if (response && response.success) {
        showSnackbar('Tax updated successfully', 'success');
        handleRefresh();
        handleClose();
      } else {
        showSnackbar(response?.message || 'Error updating tax', 'error');
      }
    } catch (error: any) {
      console.error('Error updating tax:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error updating tax';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Tax Type */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Tax Type"
            name="taxType"
            value={formData.taxType}
            onChange={handleChange}
            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          />
        </Grid>

        {/* Value */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Value *"
            name="value"
            type="number"
            value={formData.value}
            onChange={handleChange}
            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          />
        </Grid>

        {/* Calculation On */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Calculation On *</InputLabel>
            <Select
              name="calculationOn"
              value={formData.calculationOn}
              onChange={handleChange}
              label="Calculation On *"
            >
              <MenuItem value="">Select Calculation On</MenuItem>
              <MenuItem value="Basic">Basic</MenuItem>
              <MenuItem value="Subtotal">Subtotal</MenuItem>
              <MenuItem value="Total">Total</MenuItem>
              <MenuItem value="NetAmount">Net Amount</MenuItem>
              <MenuItem value="GrossAmount">Gross Amount</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Calculation Order */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Calculation Order"
            name="calculationOrder"
            type="number"
            value={formData.calculationOrder}
            onChange={handleChange}
            InputProps={{ inputProps: { min: 0 } }}
          />
        </Grid>

        {/* Tax Category */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Tax Category *</InputLabel>
            <Select
              name="taxCategory"
              value={formData.taxCategory}
              onChange={handleChange}
              label="Tax Category *"
            >
              <MenuItem value="">Select Tax Category</MenuItem>
              {taxCategories.map((category) => (
                <MenuItem key={category.id} value={category.name}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Account Name */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Account Name *</InputLabel>
            <Select
              name="accountName"
              value={formData.accountName}
              onChange={handleChange}
              label="Account Name *"
            >
              <MenuItem value="">Select Account Name</MenuItem>
              {accountNames.map((account) => (
                <MenuItem key={account.id} value={account.name}>
                  {account.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Percentage, Addition and Status */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.percentage}
                    onChange={handleSwitchChange('percentage')}
                    name="percentage"
                    color="primary"
                  />
                }
                label="Percentage"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.addition}
                    onChange={handleSwitchChange('addition')}
                    name="addition"
                    color="primary"
                  />
                }
                label="Addition"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Update'}
        </Button>
      </Box>
    </Box>
  );
};

export default EditTax;